import React, { useMemo, useState } from 'react';
import { ListChecks } from 'lucide-react';
import { fmtKwh, fmtNum, fmtPct } from '../../../../src/lib/funnelUtils';
import { statusPanelBucket } from '../statusPanelBuckets';
import { NegRow, StatusBucket, STATUS_BUCKET_LABELS } from '../types';
import NegotiationDrillDownList from './NegotiationDrillDownList';

const BUCKET_ORDER: StatusBucket[] = ['em_analise', 'com_pendencia', 'aprovados', 'reprovados', 'outros'];

interface StatusPanelProps {
    rows: NegRow[];
    total: number;
    onOpenDetail: (id: number) => void;
}

/** Painel de Status — quantidade/kWh/% do funil por status ATUAL de cada negociação,
 * dentro dos filtros de responsável/canal/cidade/origem/status já aplicados — mas
 * deliberadamente INDEPENDENTE do filtro de período: é retrato do "onde cada negociação
 * está agora", não uma coorte de quem entrou num intervalo de datas. Por isso o total pode
 * incluir negociações criadas antes do período selecionado. "Valor" não existe como campo
 * na negociação (dinheiro vive em Fatura/UC/ComissaoParceiro), por isso é exibido como N/A
 * em vez de um número inventado. */
const StatusPanel: React.FC<StatusPanelProps> = ({ rows, total, onOpenDetail }) => {
    const [expanded, setExpanded] = useState<StatusBucket | null>(null);

    const buckets = useMemo(() => {
        const acc: Record<StatusBucket, NegRow[]> = { em_analise: [], com_pendencia: [], aprovados: [], reprovados: [], outros: [] };
        rows.forEach(r => { acc[statusPanelBucket(r)].push(r); });
        return acc;
    }, [rows]);

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                <ListChecks size={15} className="text-primary" /> Painel de Status — Análise em Tempo Real
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Status atual de cada negociação, independente do período selecionado. Clique num status pra ver as negociações; clique numa negociação pra abrir o card no CRM</p>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-700">
                            <th className="py-2 pr-3 font-semibold">Status</th>
                            <th className="py-2 pr-3 font-semibold text-right">Quantidade</th>
                            <th className="py-2 pr-3 font-semibold text-right">kWh</th>
                            <th className="py-2 pr-3 font-semibold text-right">% do funil</th>
                            <th className="py-2 font-semibold text-right" title="Não existe campo de valor financeiro na negociação — dinheiro vive em Fatura/UC/Comissão">Valor</th>
                        </tr>
                    </thead>
                    <tbody>
                        {BUCKET_ORDER.map(bucket => {
                            const list = buckets[bucket];
                            const kwh = list.reduce((s, r) => s + (r.consumo_medio_kwh || 0), 0);
                            const pct = total > 0 ? (list.length / total) * 100 : 0;
                            const isExpanded = expanded === bucket;
                            return (
                                <React.Fragment key={bucket}>
                                    <tr
                                        className={`border-b border-gray-50 dark:border-gray-700/50 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${isExpanded ? 'bg-gray-50 dark:bg-gray-700/40' : ''}`}
                                        onClick={() => setExpanded(isExpanded ? null : bucket)}
                                    >
                                        <td className="py-2 pr-3 font-semibold text-gray-800 dark:text-gray-100">{STATUS_BUCKET_LABELS[bucket]}</td>
                                        <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{fmtNum(list.length)}</td>
                                        <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{fmtKwh(kwh)}</td>
                                        <td className="py-2 pr-3 text-right font-bold text-blue-600 dark:text-blue-400">{fmtPct(pct)}</td>
                                        <td className="py-2 text-right text-gray-300 dark:text-gray-600" title="N/A — não existe campo de valor financeiro na negociação">N/A</td>
                                    </tr>
                                    {isExpanded && (
                                        <tr>
                                            <td colSpan={5} className="p-0">
                                                <NegotiationDrillDownList rows={list} onOpenDetail={onOpenDetail} />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default StatusPanel;
